<section class="at">

  <header class="at__header">
    <div>
      <h1 class="at__title">
        <span class="at__title-icon"><i class="fa-solid fa-file-pen" aria-hidden="true"></i></span>
        {{ 'ADMIN_TESTS.TITLE' | translate }}
      </h1>
      <p class="at__subtitle">{{ 'ADMIN_TESTS.SUBTITLE' | translate }}</p>
    </div>

    @if (!loading() && !errored()) {
      <span class="at__total">
        {{ 'ADMIN_TESTS.COUNT' | translate: { shown: filtered().length, total: tests().length } }}
      </span>
    }
  </header>

  <!-- Facettes : « tous » par défaut partout, jamais d'option morte. -->
  <section class="at__filters" role="search">
    <header class="at__filters-head">
      <h2 class="at__filters-title">
        <i class="fa-solid fa-filter" aria-hidden="true"></i>
        {{ 'QUESTION_BANK.FILTERS.TITLE' | translate }}
        @if (hasActiveFilters()) {
          <span class="at__filters-count">
            {{ 'QUESTION_BANK.FILTERS.ACTIVE_COUNT' | translate: { count: activeCount() } }}
          </span>
        }
      </h2>

      @if (hasActiveFilters()) {
        <button type="button" class="at__reset" (click)="resetFilters()">
          <i class="fa-solid fa-rotate-left" aria-hidden="true"></i>
          {{ 'QUESTION_BANK.FILTERS.RESET' | translate }}
        </button>
      }
    </header>

    <div class="at__search">
      <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
      <input type="search"
             [value]="search()"
             (input)="onSearch($any($event.target).value)"
             [attr.aria-label]="'ADMIN_TESTS.SEARCH' | translate"
             [placeholder]="'ADMIN_TESTS.SEARCH_PLACEHOLDER' | translate">
    </div>

    <div class="at__grid">
      <label class="at__field">
        <span class="at__label">{{ 'ADMIN_TESTS.PROFESSOR' | translate }}</span>
        <select [value]="professorId() ?? ''"
                [disabled]="professorOptions().length === 0"
                (change)="onProfessor($any($event.target).value)">
          <option value="">{{ 'ADMIN_TESTS.ALL_PROFESSORS' | translate }}</option>
          @for (p of professorOptions(); track p.id) {
            <option [value]="p.id">{{ p.label }}</option>
          }
        </select>
      </label>

      <label class="at__field">
        <span class="at__label">{{ 'QUESTION_BANK.FILTERS.LEARNING_PATH' | translate }}</span>
        <select [value]="learningPathId() ?? ''"
                [disabled]="learningPathOptions().length === 0"
                (change)="onLearningPath($any($event.target).value)">
          <option value="">{{ 'QUESTION_BANK.FILTERS.ALL_LEARNING_PATHS' | translate }}</option>
          @for (p of learningPathOptions(); track p.id) {
            <option [value]="p.id">{{ p.label }}</option>
          }
        </select>
      </label>

      <label class="at__field">
        <span class="at__label">{{ 'QUESTION_BANK.FILTERS.SUBJECT' | translate }}</span>
        <select [value]="subjectId() ?? ''"
                [disabled]="subjectOptions().length === 0"
                (change)="onSubject($any($event.target).value)">
          <option value="">{{ 'QUESTION_BANK.FILTERS.ALL_SUBJECTS' | translate }}</option>
          @for (s of subjectOptions(); track s.id) {
            <option [value]="s.id">{{ s.label }}</option>
          }
        </select>
      </label>

      <label class="at__field">
        <span class="at__label">{{ 'DATA_TABLE.MODE' | translate }}</span>
        <select [value]="mode() ?? ''" (change)="onMode($any($event.target).value)">
          <option value="">{{ 'ADMIN.STATUS_ALL' | translate }}</option>
          @for (m of modes; track m) {
            <option [value]="m">{{ modeKey(m) | translate }}</option>
          }
        </select>
      </label>
    </div>
  </section>

  <!-- Échec de la requête : on le dit, avec un moyen de réessayer.
       Aucune donnée n'est affichée à la place. -->
  @if (errored()) {
    <app-error-state
      titleKey="ADMIN_TESTS.ERROR_TITLE"
      messageKey="ADMIN_TESTS.ERROR_MESSAGE"
      [detail]="errorDetail()"
      (retry)="load()">
    </app-error-state>
  }

  @if (!errored()) {
    <div class="at__table-wrap">
      <table class="at__table">
        <thead>
          <tr>
            <th class="at__th">{{ 'DATA_TABLE.TITLE' | translate }}</th>
            <th class="at__th">{{ 'ADMIN_TESTS.PROFESSOR' | translate }}</th>
            <th class="at__th">{{ 'DATA_TABLE.LEARNING_PATH' | translate }}</th>
            <th class="at__th">{{ 'DATA_TABLE.SUBJECT' | translate }}</th>
            <th class="at__th">{{ 'DATA_TABLE.MODE' | translate }}</th>
            <th class="at__th at__th--num">{{ 'DATA_TABLE.QUESTION_COUNT' | translate }}</th>
            <th class="at__th at__th--num">{{ 'TEST_DETAIL.SCALE_F' | translate }}</th>
            <th class="at__th">{{ 'ADMIN_TESTS.STATUS_COL' | translate }}</th>
            <th class="at__th">{{ 'DATA_TABLE.START_DATE' | translate }}</th>
            <th class="at__th at__th--num">{{ 'DATA_TABLE.ACTIONS' | translate }}</th>
          </tr>
        </thead>

        <tbody>
          @if (loading()) {
            @for (row of [0,1,2,3,4,5]; track row) {
              <tr>
                @for (cell of [0,1,2,3,4,5,6,7,8,9]; track cell) {
                  <td class="at__skel-cell"><span class="at__skel-bar"></span></td>
                }
              </tr>
            }
          } @else {
            @for (test of paged(); track test.id) {
              <tr class="at__row" [class.is-busy]="busyId() === test.id">
                <td class="at__td at__td--title">
                  <button type="button" class="at__link" (click)="openDetail(test)">
                    {{ test.title }}
                  </button>
                </td>
                <td class="at__td">{{ professorLabel(test) }}</td>
                <td class="at__td">{{ learningPathLabel(test) }}</td>
                <td class="at__td">{{ test.subjectName || '—' }}</td>
                <td class="at__td">{{ modeKey(test.testMode) | translate }}</td>
                <td class="at__td at__td--num">{{ test.questionsCount }}</td>
                <td class="at__td at__td--num">{{ test.gradingScale || 20 }}</td>
                <td class="at__td">
                  <span class="at__badge" [class.is-published]="test.status === 'published'">
                    {{ statusKey(test.status) | translate }}
                  </span>
                </td>
                <td class="at__td">{{ test.startDate ? (test.startDate | date: 'dd/MM/yyyy') : '—' }}</td>
                <td class="at__td at__td--actions">
                  <button type="button" class="at__action"
                          [disabled]="busyId() === test.id"
                          (click)="togglePublish(test)"
                          [title]="(test.status === 'published' ? 'BUTTONS.UNPUBLISH' : 'BUTTONS.PUBLISH') | translate">
                    <i class="fa-solid"
                       [class.fa-eye-slash]="test.status === 'published'"
                       [class.fa-upload]="test.status !== 'published'"
                       aria-hidden="true"></i>
                  </button>
                  <button type="button" class="at__action"
                          [disabled]="busyId() === test.id"
                          (click)="editTest(test)"
                          [title]="'BUTTONS.EDIT' | translate">
                    <i class="fa-solid fa-pen" aria-hidden="true"></i>
                  </button>
                  <button type="button" class="at__action at__action--danger"
                          [disabled]="busyId() === test.id"
                          (click)="deleteTest(test)"
                          [title]="'BUTTONS.DELETE' | translate">
                    <i class="fa-solid fa-trash" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>

    @if (totalPages() > 1) {
      <nav class="at__pagination" [attr.aria-label]="'ADMIN_TESTS.PAGINATION' | translate">
        <button type="button" class="at__page-btn" [disabled]="!canPrevious()" (click)="previousPage()">
          <i class="fa-solid fa-chevron-left at__chev" aria-hidden="true"></i>
          {{ 'QUESTION_BANK.PAGINATION.PREVIOUS' | translate }}
        </button>
        <span class="at__page-of">
          {{ 'QUESTION_BANK.PAGINATION.PAGE_OF' | translate: { current: currentPage(), total: totalPages() } }}
        </span>
        <button type="button" class="at__page-btn" [disabled]="!canNext()" (click)="nextPage()">
          {{ 'QUESTION_BANK.PAGINATION.NEXT' | translate }}
          <i class="fa-solid fa-chevron-right at__chev" aria-hidden="true"></i>
        </button>
      </nav>
    }

    @if (isEmpty()) {
      <app-empty-state
        icon="file-circle-question"
        [titleKey]="hasActiveFilters() ? 'ADMIN_TESTS.EMPTY_FILTERED_TITLE' : 'ADMIN_TESTS.EMPTY_TITLE'"
        [messageKey]="hasActiveFilters() ? 'ADMIN_TESTS.EMPTY_FILTERED_MESSAGE' : 'ADMIN_TESTS.EMPTY_MESSAGE'">
      </app-empty-state>
    }
  }

</section>
